import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSubscriptions, usePackages } from '../../hooks/usePackages';
import { db } from '../../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Organization } from '../../types/organization';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilSquareIcon,
  PlayIcon,
  PauseIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  CloudArrowUpIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import { OrganizationSubscription, Package } from '../../types/package';
import { formatCurrency } from '../../utils/formatters';
import { formatDistanceToNow } from 'date-fns';
import { hu } from 'date-fns/locale';
import Modal from '../common/Modal';

interface SubscriptionManagerProps {
  subscriptions?: OrganizationSubscription[];
  packages?: Package[];
  onSubscriptionUpdate?: (id: string, data: Partial<OrganizationSubscription>) => void;
  onSubscriptionCreate?: (data: Omit<OrganizationSubscription, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({
  subscriptions: propSubscriptions = [],
  packages: propPackages = [],
  onSubscriptionUpdate,
  onSubscriptionCreate
}) => {
  const { t } = useLanguage();
  const { subscriptions: hookSubscriptions, loading: subscriptionsLoading, updateSubscription } = useSubscriptions();
  const { packages: hookPackages, loading: packagesLoading } = usePackages();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedSubscription, setSelectedSubscription] = useState<OrganizationSubscription | null>(null);
  const [organizations, setOrganizations] = useState<Record<string, Organization>>({});

  // Fetch organizations for name lookup
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'organizations'));
        const orgMap: Record<string, Organization> = {};
        snapshot.docs.forEach(doc => {
          orgMap[doc.id] = { id: doc.id, ...doc.data() } as Organization;
        });
        setOrganizations(orgMap);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };
    fetchOrgs();
  }, []);

  const getOrgName = useMemo(() => {
    return (orgId: string) => organizations[orgId]?.name || orgId;
  }, [organizations]);

  // Demo adatok, ha nincs adat betöltve
  const [demoSubscriptions] = useState<OrganizationSubscription[]>([
    {
      id: 'sub_1',
      organizationId: 'org_1',
      packageId: 'pkg_2',
      status: 'active',
      startDate: new Date('2024-01-01'),
      endDate: new Date('2025-01-01'),
      autoRenew: true,
      paymentMethod: 'card',
      lastPaymentDate: new Date('2024-12-01'),
      nextPaymentDate: new Date('2025-01-01'),
      usage: {
        users: 8,
        storage: 15.5,
        apiCalls: 1250,
        lastUpdated: new Date()
      },
      createdAt: new Date('2024-01-01'),
      updatedAt: new Date('2024-12-01')
    },
    {
      id: 'sub_2',
      organizationId: 'org_2',
      packageId: 'pkg_3',
      status: 'trial',
      startDate: new Date('2024-12-01'),
      trialEndDate: new Date('2024-12-31'),
      autoRenew: false,
      usage: {
        users: 25,
        storage: 45.2,
        apiCalls: 3500,
        lastUpdated: new Date()
      },
      createdAt: new Date('2024-12-01'),
      updatedAt: new Date('2024-12-15')
    },
    {
      id: 'sub_3',
      organizationId: 'org_3',
      packageId: 'pkg_1',
      status: 'suspended',
      startDate: new Date('2024-06-01'),
      endDate: new Date('2024-12-01'),
      autoRenew: false,
      usage: {
        users: 2,
        storage: 0.8,
        apiCalls: 150,
        lastUpdated: new Date()
      },
      createdAt: new Date('2024-06-01'),
      updatedAt: new Date('2024-11-15')
    }
  ]);

  const [demoPackages] = useState<Package[]>([
    {
      id: 'pkg_1',
      name: 'Ingyenes',
      description: 'Alapvető funkciók',
      type: 'free',
      price: { monthly: 0, yearly: 0, currency: 'HUF' },
      maxUsers: 2,
      maxOrganizations: 1,
      maxStorage: 1,
      features: [],
      modules: ['customers', 'settings'],
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 'pkg_2',
      name: 'Alap',
      description: 'Teljes CRM funkciók',
      type: 'basic',
      price: { monthly: 9900, yearly: 99000, currency: 'HUF' },
      maxUsers: 10,
      maxOrganizations: 1,
      maxStorage: 10,
      features: [],
      modules: ['customers', 'products', 'quotations', 'settings'],
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date()
    },
    {
      id: 'pkg_3',
      name: 'Professzionális',
      description: 'Fejlett funkciók',
      type: 'pro',
      price: { monthly: 69990, yearly: 839880, currency: 'HUF' },
      maxUsers: 50,
      maxOrganizations: 3,
      maxStorage: 50,
      features: [],
      modules: ['customers', 'products', 'quotations', 'projects', 'billing', 'settings'],
      isActive: true,
      isPopular: true,
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ]);

  // Használjuk a prop-okat vagy a hook-ból jövő adatokat
  const subscriptions = propSubscriptions.length > 0 ? propSubscriptions : hookSubscriptions;
  const packages = propPackages.length > 0 ? propPackages : hookPackages;
  const loading = subscriptionsLoading || packagesLoading;

  // Ha nincs adat, használjuk a demo adatokat
  const allSubscriptions = subscriptions.length > 0 ? subscriptions : demoSubscriptions;
  const allPackages = packages.length > 0 ? packages : demoPackages;

  const getPackageById = (packageId: string) => {
    return allPackages.find(pkg => pkg.id === packageId);
  };

  const getStatusColor = (status: OrganizationSubscription['status']) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'trial': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'suspended': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'inactive': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: OrganizationSubscription['status']) => {
    switch (status) {
      case 'active': return <CheckCircleIcon className="h-4 w-4" />;
      case 'trial': return <ClockIcon className="h-4 w-4" />;
      case 'suspended': return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'cancelled': return <XCircleIcon className="h-4 w-4" />;
      case 'inactive': return <PauseIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  const getStatusText = (status: OrganizationSubscription['status']) => {
    switch (status) {
      case 'active': return 'Aktív';
      case 'trial': return 'Próbaidő';
      case 'suspended': return 'Felfüggesztve';
      case 'cancelled': return 'Lemondva';
      case 'inactive': return 'Inaktív';
      default: return 'Ismeretlen';
    }
  };

  const filteredSubscriptions = allSubscriptions.filter(sub => {
    const orgName = getOrgName(sub.organizationId).toLowerCase();
    const matchesSearch = searchTerm === '' || 
      orgName.includes(searchTerm.toLowerCase()) ||
      sub.organizationId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleStatusChange = async (subscriptionId: string, newStatus: OrganizationSubscription['status']) => {
    try {
      if (onSubscriptionUpdate) {
        onSubscriptionUpdate(subscriptionId, { status: newStatus });
      } else {
        await updateSubscription(subscriptionId, { status: newStatus });
      }
    } catch (error) {
      console.error('Error updating subscription status:', error);
    }
  };

  const handlePackageChange = async (subscriptionId: string, newPackageId: string) => {
    try {
      if (onSubscriptionUpdate) {
        onSubscriptionUpdate(subscriptionId, { packageId: newPackageId });
      } else {
        await updateSubscription(subscriptionId, { packageId: newPackageId });
      }
      // Update local state
      setSelectedSubscription(prev => prev ? { ...prev, packageId: newPackageId } : null);
    } catch (error) {
      console.error('Error updating package:', error);
    }
  };

  const handleAutoRenewToggle = async (subscriptionId: string, autoRenew: boolean) => {
    try {
      if (onSubscriptionUpdate) {
        onSubscriptionUpdate(subscriptionId, { autoRenew });
      } else {
        await updateSubscription(subscriptionId, { autoRenew });
      }
      setSelectedSubscription(prev => prev ? { ...prev, autoRenew } : null);
    } catch (error) {
      console.error('Error updating auto-renew:', error);
    }
  };

  const getUsagePercentage = (used: number, max: number) => {
    if (max === -1) return 0; // Korlátlan
    return Math.min((used / max) * 100, 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600 bg-red-100';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Előfizetések kezelése</h2>
          <p className="text-gray-600 mt-1">Szervezetek előfizetéseinek és használatának nyomon követése</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Keresés szervezet neve vagy ID alapján..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-4 w-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">Minden státusz</option>
            <option value="active">Aktív</option>
            <option value="trial">Próbaidő</option>
            <option value="suspended">Felfüggesztve</option>
            <option value="cancelled">Lemondva</option>
            <option value="inactive">Inaktív</option>
          </select>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Aktív előfizetések</p>
              <p className="text-2xl font-bold text-gray-900">
                {allSubscriptions.filter(s => s.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <ClockIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Próbaidős</p>
              <p className="text-2xl font-bold text-gray-900">
                {allSubscriptions.filter(s => s.status === 'trial').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Felfüggesztve</p>
              <p className="text-2xl font-bold text-gray-900">
                {allSubscriptions.filter(s => s.status === 'suspended').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center">
            <CreditCardIcon className="h-8 w-8 text-purple-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Havi bevétel</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(
                  allSubscriptions
                    .filter(s => s.status === 'active')
                    .reduce((total, sub) => {
                      const pkg = getPackageById(sub.packageId);
                      return total + (pkg?.price.monthly || 0);
                    }, 0),
                  'HUF'
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Előfizetések betöltése...</p>
        </div>
      )}

      {/* Subscriptions Table */}
      {!loading && (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Előfizetések ({filteredSubscriptions.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Szervezet
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Csomag
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Státusz
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Használat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lejárat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Műveletek
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscriptions.map((subscription) => {
                const pkg = getPackageById(subscription.packageId);
                const userUsage = pkg ? getUsagePercentage(subscription.usage.users, pkg.maxUsers) : 0;
                const storageUsage = getUsagePercentage(subscription.usage.storage, pkg?.maxStorage || 0);

                return (
                  <tr key={subscription.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getOrgName(subscription.organizationId)}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {subscription.organizationId}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {pkg?.name || 'Ismeretlen csomag'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {pkg ? formatCurrency(pkg.price.monthly, pkg.price.currency) : '-'}/hó
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(subscription.status)}`}>
                        {getStatusIcon(subscription.status)}
                        <span className="ml-1">{getStatusText(subscription.status)}</span>
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center text-xs">
                          <UserGroupIcon className="h-3 w-3 mr-1" />
                          <span className={`px-1 rounded ${getUsageColor(userUsage)}`}>
                            {subscription.usage.users}/{pkg?.maxUsers === -1 ? '∞' : pkg?.maxUsers} felhasználó
                          </span>
                        </div>
                        <div className="flex items-center text-xs">
                          <CloudArrowUpIcon className="h-3 w-3 mr-1" />
                          <span className={`px-1 rounded ${getUsageColor(storageUsage)}`}>
                            {subscription.usage.storage.toFixed(1)}/{pkg?.maxStorage} GB
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {subscription.status === 'trial' && subscription.trialEndDate ? (
                        <div>
                          <div className="font-medium text-blue-600">Próbaidő</div>
                          <div>{formatDistanceToNow(subscription.trialEndDate, { addSuffix: true, locale: hu })
                            .replace('körülbelül ', '')
                            .replace(' ezelőtt', '')
                            .replace('órával', 'órája')
                            .replace('nappal', 'napja')
                            .replace('héttel', 'hete')
                            .replace('hónappal', 'hónapja')
                            .replace('évvel', 'éve')
                            .replace('perccel', 'perce')
                            .replace('másodperccel', 'másodperce')}</div>
                        </div>
                      ) : subscription.endDate ? (
                        <div>
                          <div className={subscription.autoRenew ? 'text-green-600' : 'text-gray-600'}>
                            {subscription.autoRenew ? 'Auto-megújítás' : 'Egyszeri'}
                          </div>
                          <div>{formatDistanceToNow(subscription.endDate, { addSuffix: true, locale: hu })
                            .replace('körülbelül ', '')
                            .replace(' ezelőtt', '')
                            .replace('órával', 'órája')
                            .replace('nappal', 'napja')
                            .replace('héttel', 'hete')
                            .replace('hónappal', 'hónapja')
                            .replace('évvel', 'éve')
                            .replace('perccel', 'perce')
                            .replace('másodperccel', 'másodperce')}</div>
                        </div>
                      ) : (
                        'Korlátlan'
                      )}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedSubscription(subscription)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        
                        {subscription.status === 'active' && (
                          <button
                            onClick={() => handleStatusChange(subscription.id, 'suspended')}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Felfüggesztés"
                          >
                            <PauseIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        {subscription.status === 'suspended' && (
                          <button
                            onClick={() => handleStatusChange(subscription.id, 'active')}
                            className="text-green-600 hover:text-green-900"
                            title="Aktiválás"
                          >
                            <PlayIcon className="h-4 w-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => setSelectedSubscription(subscription)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Szerkesztés"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredSubscriptions.length === 0 && (
          <div className="text-center py-12">
            <CreditCardIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nincs előfizetés</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Nincs a szűrési feltételeknek megfelelő előfizetés.'
                : 'Még nincs regisztrált előfizetés a rendszerben.'
              }
            </p>
          </div>
        )}
      </div>
      )}

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedSubscription}
        onClose={() => setSelectedSubscription(null)}
        title="Előfizetés részletei"
        size="2xl"
      >
        {selectedSubscription && (
          <div className="space-y-6">
                {/* Organization */}
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <BuildingOfficeIcon className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {getOrgName(selectedSubscription.organizationId)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {selectedSubscription.organizationId}
                    </div>
                  </div>
                </div>

                {/* Package & Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Csomag</div>
                    <select
                      value={selectedSubscription.packageId}
                      onChange={(e) => handlePackageChange(selectedSubscription.id, e.target.value)}
                      className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    >
                      {allPackages.map(pkg => (
                        <option key={pkg.id} value={pkg.id}>
                          {pkg.name} — {formatCurrency(pkg.price.monthly, pkg.price.currency)}/hó
                        </option>
                      ))}
                    </select>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {(() => {
                        const pkg = getPackageById(selectedSubscription.packageId);
                        return pkg ? `${pkg.maxUsers} felh. · ${pkg.maxStorage} GB` : '-';
                      })()}
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Státusz</div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedSubscription.status)}`}>
                        {getStatusIcon(selectedSubscription.status)}
                        <span className="ml-1">{getStatusText(selectedSubscription.status)}</span>
                      </span>
                    </div>
                    <label className="flex items-center space-x-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSubscription.autoRenew}
                        onChange={(e) => handleAutoRenewToggle(selectedSubscription.id, e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400">Auto-megújítás</span>
                    </label>
                  </div>
                </div>

                {/* Usage */}
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Használat</div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <UserGroupIcon className="h-3 w-3 mr-1" /> Felhasználók
                      </div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {selectedSubscription.usage.users}/{getPackageById(selectedSubscription.packageId)?.maxUsers === -1 ? '∞' : getPackageById(selectedSubscription.packageId)?.maxUsers || '-'}
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <CloudArrowUpIcon className="h-3 w-3 mr-1" /> Tárhely
                      </div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {selectedSubscription.usage.storage.toFixed(1)}/{getPackageById(selectedSubscription.packageId)?.maxStorage || '-'} GB
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">API hívások</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {selectedSubscription.usage.apiCalls.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Kezdés</div>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {selectedSubscription.startDate.toLocaleDateString('hu-HU')}
                    </div>
                  </div>
                  {selectedSubscription.status === 'trial' && selectedSubscription.trialEndDate ? (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Próbaidő vége</div>
                      <div className="text-sm text-blue-600 dark:text-blue-400">
                        {selectedSubscription.trialEndDate.toLocaleDateString('hu-HU')}
                      </div>
                    </div>
                  ) : selectedSubscription.endDate ? (
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Lejárat</div>
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {selectedSubscription.endDate.toLocaleDateString('hu-HU')}
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  {selectedSubscription.status === 'active' && (
                    <button
                      onClick={() => {
                        handleStatusChange(selectedSubscription.id, 'suspended');
                        setSelectedSubscription(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-yellow-700 bg-yellow-100 hover:bg-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/30 rounded-md"
                    >
                      <PauseIcon className="h-4 w-4 inline mr-1" />
                      Felfüggesztés
                    </button>
                  )}
                  {selectedSubscription.status === 'suspended' && (
                    <button
                      onClick={() => {
                        handleStatusChange(selectedSubscription.id, 'active');
                        setSelectedSubscription(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900/30 rounded-md"
                    >
                      <PlayIcon className="h-4 w-4 inline mr-1" />
                      Aktiválás
                    </button>
                  )}
                  {selectedSubscription.status === 'trial' && (
                    <button
                      onClick={() => {
                        handleStatusChange(selectedSubscription.id, 'active');
                        setSelectedSubscription(null);
                      }}
                      className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900/30 rounded-md"
                    >
                      <CheckCircleIcon className="h-4 w-4 inline mr-1" />
                      Próbaidő lezárása & Aktiválás
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedSubscription(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Bezárás
                  </button>
                </div>
              </div>
        )}
      </Modal>

    </div>
  );
};

export default SubscriptionManager;
